import { useForm } from '@mantine/form';
import ProjectTimeline from '../../../components/common/ProjectTimeline/ProjectTimeline';
import SectionTitle from '../../../components/common/SectionTitle/SectionTitle';
import UploadMultipleFiles from '../../../components/common/UploadFiles/UploadMultipleFiles';
import GlobalButton from '../../../components/ui/GlobalButton';
import Paper from '../../../components/ui/Paper/Paper';
import BoxInput from '../../../components/ui/BoxInput/BoxInput';
import TextInput from '../../../components/ui/TextInput/TextInput';
import PageWrap from '../../../layouts/PageWrap/PageWrap';
import { projectSchema } from '../../../schemas/projects';
import { zodResolverCustom } from '../../../utils/zodResolverCustom';
import Breadcrumb from '../../../components/common/Breadcrumb/Breadcrumb';
import { useEffect, useState } from 'react';
import useAuthStore from '../../../store/Auth';
import WebAppService from '../../../services/WebAppService';
import { useModal } from '../../../context/ModalContext';
import usePollingStore from '../../../store/Polling';
import { useLocation, useNavigate } from 'react-router-dom';
import LoadingComponent from '../../../components/LoadingComponent';
import SelectInput from '../../../components/ui/SelectInput/SelectInput';
import AddReferenceLinks from '../../../components/common/AddReferenceLinks/AddReferenceLinks';
import { Slack } from '../../../svgs/logos';
import JiraService from '@/services/JiraService';
import { getErrorMessage } from '@/utils/helpers/helpers';
import {
  clearJiraImportDraft,
  getJiraImportDraft,
  JiraImportDraft,
  saveJiraImportDraft,
} from '@/components/modals/SelectCreateProjectFlowModal/JiraImport/sessionStorage';
import JiraImportDetails from './JiraImportDetails';

const breadcrumbItems = [
  { label: 'Projects', link: '/projects' },
  {
    label: `Create New Project`,
    link: `/projects/create`,
    isActive: true,
  },
];

export const priorityOptions = [
  {
    icon: <div className="bg-[#4DAD52] w-5 h-5 rounded-[5px]"></div>,
    label: 'P0',
    value: 'P0',
  },
  {
    icon: <div className="bg-[#9BA735] w-5 h-5 rounded-[5px]"></div>,
    label: 'P1',
    value: 'P1',
  },
  {
    icon: <div className="bg-[#954281] w-5 h-5 rounded-[5px]"></div>,
    label: 'P2',
    value: 'P2',
  },
  {
    icon: <div className="bg-[#225668] w-5 h-5 rounded-[5px]"></div>,
    label: 'P3',
    value: 'P3',
  },
];

interface CreateProjectLocationState {
  projectLimitChecked?: boolean;
  jiraImport?: JiraImportDraft;
}

export default function CreateProject() {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal, closeModal } = useModal();
  const { setCreateTicketsResponse } = usePollingStore();
  const { organization, isPilot } = useAuthStore();
  const routeState = location.state as CreateProjectLocationState | null;
  const jiraImport = routeState?.jiraImport ?? getJiraImportDraft();
  const isJiraImport = Boolean(jiraImport?.projectKey && jiraImport?.epicKey);
  const hasPrecheckedProjectLimit = Boolean(routeState?.projectLimitChecked);
  const [loading, setLoading] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(
    !isPilot && !hasPrecheckedProjectLimit
  );
  const jiraImportPreparedTasks =
    jiraImport?.preview?.total_crafter_tasks ??
    jiraImport?.preview?.total_jira_tasks ??
    0;

  useEffect(() => {
    if (routeState?.jiraImport) {
      saveJiraImportDraft(routeState.jiraImport);
    }
  }, [routeState?.jiraImport]);

  useEffect(() => {
    if (isPilot || hasPrecheckedProjectLimit || !organization?.id) {
      setCheckingLimit(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await WebAppService.getProjects(organization.id, {
          page: 1,
          limit: 1,
        });
        if (cancelled) return;
        const total =
          response?.meta?.total ??
          response?.total ??
          response?.data?.length ??
          0;
        if (total >= 1) {
          openModal('create-project-error');
          navigate('/projects', { replace: true });
          return;
        }
      } catch (error) {
        console.error('Failed to verify project limit', error);
      } finally {
        if (!cancelled) setCheckingLimit(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasPrecheckedProjectLimit, isPilot, organization?.id]);
  const form = useForm<{
    name: string;
    description: string;
    slack_channel: string;
    priority: string;
    start_date: string;
    end_date: string;
    files: File[];
    prd_file: File | Record<string, never>;
    tech_spec_file: File | Record<string, never>;
    reference_links: any[];
  }>({
    initialValues: {
      name: jiraImport?.preview?.project_prefill?.name || '',
      description: jiraImport?.preview?.project_prefill?.description || '',
      slack_channel: '',
      priority: '',
      start_date: '',
      end_date: '',
      files: [],
      prd_file: {},
      tech_spec_file: {},
      reference_links: [],
    },
    validate: zodResolverCustom(projectSchema),

    validateInputOnBlur: true,
  });

  const handleSubmit = async () => {
    setLoading(true);
    openModal('create-project');

    try {
      let res;

      if (isJiraImport && jiraImport) {
        res = await JiraService.createProjectFromJira({
          name: form.values.name,
          description: form.values.description,
          slack_channel: form.values.slack_channel,
          priority: form.values.priority,
          start_date: form.values.start_date,
          end_date: form.values.end_date,
          files: form.values.files,
          reference_links: form.values.reference_links,
          jira_project_key: jiraImport.projectKey,
          jira_epic_key: jiraImport.epicKey,
          model_type: 'openai',
        });
      } else {
        const formData = new FormData();

        formData.append('name', form.values.name);
        formData.append('description', form.values.description);
        formData.append('slack_channel', form.values.slack_channel);
        formData.append('start_date', form.values.start_date);
        formData.append('end_date', form.values.end_date);
        formData.append('priority', form.values.priority);
        formData.append(
          'reference_links',
          JSON.stringify(form.values.reference_links)
        );

        form.values.files.forEach((file) => {
          formData.append('files', file);
        });

        res = await WebAppService.createProjectFromFiles(
          organization.id,
          formData
        );
      }

      const normalized = {
        ...res,
        project: { project: res?.project },
      };
      setCreateTicketsResponse(normalized);
      if (isJiraImport) {
        clearJiraImportDraft();
      }
      navigate('/projects');
    } catch (error: any) {
      setLoading(false);
      closeModal('create-project');
      const message = getErrorMessage(
        error,
        isJiraImport
          ? 'Something went wrong while importing this Jira epic.'
          : 'Something went wrong while creating your project.'
      );
      openModal('create-project-error', { error: message });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  if (checkingLimit) {
    return (
      <PageWrap title="Create Project">
        <div className="flex items-center justify-center w-full h-full py-20">
          <LoadingComponent />
        </div>
      </PageWrap>
    );
  }

  return (
    <PageWrap title="Create Project">
      <form
        onSubmit={form.onSubmit(handleSubmit)}
        className="flex relative flex-col gap-6 w-full h-full px-0  rounded-md overflow-auto pt-6"
      >
        <Breadcrumb items={breadcrumbItems} />
        <div className="flex flex-col gap-4 w-full px-4 py-4 lg:py-6 lg:px-8 rounded-xl bg-frame-inner">
          <div className="flex w-full flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <SectionTitle
              title={'Create a new project'}
              description={
                'Create a centralized workspace for your project by adding requirements, timelines, supporting documents to help your team plan and execute effectively.'
              }
            />
            <div className="w-fit">
              <GlobalButton
                type={'submit'}
                variant={'purple'}
                size={'md'}
                state={loading ? 'inactive' : 'default'}
              >
                {loading ? 'Creating' : 'Create Project'}
              </GlobalButton>
            </div>
          </div>
          {isJiraImport && (
            <div className="flex flex-wrap items-center text-sm font-semibold text-text-semantics-caption">
              <span>
                Importing {jiraImportPreparedTasks} task
                {jiraImportPreparedTasks === 1 ? '' : 's'} from Jira.
              </span>
              {jiraImport && <JiraImportDetails jiraImport={jiraImport} />}
            </div>
          )}
        </div>

        <Paper radius={12}>
          <div className="w-2/3 xl:w-[800px] max-[1200px] flex flex-col gap-5 mx-auto py-6">
            <TextInput
              size="lg"
              isRequired
              label={`Project Name`}
              placeholder={`Enter a descriptive project title`}
              {...form.getInputProps('name')}
            />

            <BoxInput
              label="Project Description"
              isRequired
              placeholder="Write a description or project brief"
              minHeight={160}
              value={form.values.description}
              onChange={(html) => form.setFieldValue('description', html)}
              onBlur={() => form.validateField('description')}
              error={form.errors.description as string}
              showToolbar={false}
            />
            <UploadMultipleFiles
              files={form.values.files}
              onChange={(files) => form.setFieldValue('files', files)}
              error={form.errors.files as string}
              onBlur={() => form.validateField('files')}
            />

            <AddReferenceLinks {...form.getInputProps('reference_links')} />

            <SelectInput
              size="lg"
              isRequired
              label={'Project Priority'}
              placeholder="Select priority"
              options={priorityOptions}
              {...form.getInputProps('priority')}
            />

            <ProjectTimeline
              startDate={form.values.start_date}
              endDate={form.values.end_date}
              onChangeStartDate={(value) => {
                form.setFieldValue('start_date', value);
                form.clearFieldError('start_date');
              }}
              onChangeEndDate={(value) => {
                form.setFieldValue('end_date', value);
                form.clearFieldError('end_date');
              }}
              errorStartDate={form.errors.start_date as string}
              errorEndDate={form.errors.end_date as string}
            />

            <TextInput
              size="lg"
              leftSection={<Slack />}
              prefix={''}
              placeholder={'Enter Slack channel URL'}
              label={'Project Slack Channel'}
              {...form.getInputProps('slack_channel')}
            />

            <div className="ml-auto mt-3">
              <GlobalButton
                type={'submit'}
                variant={'purple'}
                size={'md'}
                state={loading ? 'inactive' : 'default'}
              >
                {loading ? 'Creating' : 'Create Project'}
              </GlobalButton>
            </div>
          </div>
        </Paper>
      </form>
    </PageWrap>
  );
}
